import React, { useRef, useState } from "react";
import { Box, Typography, Paper, IconButton, Tooltip, LinearProgress, Button, Fade } from "@mui/material";
import { keyframes } from "@mui/system";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { BORDER, TEXT_PRIMARY, TEXT_SECONDARY, formatFileSize } from "./constants";

const popIn = keyframes`
  0% { transform: scale(0.4); opacity: 0; }
  60% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); }
`;

const StatusLine = ({ icon, color, pop, children }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, color, mt: 1.25 }}>
    {React.cloneElement(icon, {
      sx: { fontSize: 18, mt: "1px", flexShrink: 0, animation: pop ? `${popIn} .4s ease-out` : "none" },
    })}
    <Typography variant="body2" sx={{ color, fontWeight: 500, lineHeight: 1.5 }}>
      {children}
    </Typography>
  </Box>
);

const ValidationStatus = ({ entry }) => {
  if (entry.status === "validating") {
    return (
      <Box sx={{ mt: 1.5 }}>
        <LinearProgress sx={{ borderRadius: 1, height: 4 }} />
        <Typography variant="caption" color={TEXT_SECONDARY} sx={{ mt: 0.75, display: "block" }}>
          בודק שהקובץ תקין ומכיל שכבת טקסט…
        </Typography>
      </Box>
    );
  }
  if (entry.status === "invalid") {
    return (
      <StatusLine icon={<ErrorOutlineIcon />} color="#b91c1c">
        {entry.error}
      </StatusLine>
    );
  }
  if (entry.status === "valid") {
    const { pageCount, pagesWithoutText = [] } = entry.info;
    return (
      <>
        <StatusLine icon={<CheckCircleIcon />} color="#15803d" pop>
          שכבת טקסט תקינה · {pageCount} {pageCount === 1 ? "עמוד" : "עמודים"}
          {entry.info.sectionCount > 0 && ` · ${entry.info.sectionCount} סעיפים זוהו`}
        </StatusLine>
        {pagesWithoutText.length > 0 && (
          <StatusLine icon={<WarningAmberIcon />} color="#b45309">
            {pagesWithoutText.length === 1 ? "בעמוד" : "בעמודים"} {pagesWithoutText.join(", ")} לא נמצא
            טקסט — לא ייכללו בהשוואה
          </StatusLine>
        )}
        {/* The file has little or no packaging text (a proof, outlined text) */}
        {entry.info.textWarning && (
          <StatusLine icon={<WarningAmberIcon />} color="#b45309">
            {entry.info.textWarning}
          </StatusLine>
        )}
      </>
    );
  }
  return null;
};

const PdfVersionCard = ({ version, entry, onSelect, onClear, disabled }) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const hasFile = Boolean(entry?.file);

  const openPicker = () => !disabled && inputRef.current?.click();

  const handleFiles = (files) => {
    const file = files?.[0];
    if (file) onSelect(file);
  };

  const dragHandlers = {
    onDragOver: (e) => {
      e.preventDefault();
      if (!disabled) setIsDragging(true);
    },
    onDragLeave: (e) => {
      e.preventDefault();
      if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false);
    },
    onDrop: (e) => {
      e.preventDefault();
      setIsDragging(false);
      if (!disabled) handleFiles(e.dataTransfer.files);
    },
  };

  const borderColor = isDragging
    ? version.color
    : entry?.status === "invalid"
      ? "#fca5a5"
      : entry?.status === "valid"
        ? `${version.color}66`
        : BORDER;

  return (
    <Paper
      elevation={0}
      {...dragHandlers}
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: 2,
        border: `1px solid ${borderColor}`,
        // Thin version-coloured strip along the top
        borderTop: `3px solid ${version.color}`,
        bgcolor: isDragging ? version.bg : "#fff",
        // Lifts slightly while a file is dragged over it
        transform: isDragging ? "translateY(-3px)" : "none",
        boxShadow: isDragging ? `0 12px 28px ${version.color}2e, 0 0 0 4px ${version.color}1f` : "none",
        transition: "border-color .15s, box-shadow .2s, background-color .15s, transform .2s",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {/* Version label */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: version.color, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography fontWeight={700} color={TEXT_PRIMARY} lineHeight={1.3}>
            {version.label}
          </Typography>
          <Typography variant="caption" color={TEXT_SECONDARY}>
            {version.hint}
          </Typography>
        </Box>
      </Box>

      {!hasFile ? (
        <Box
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={`בחירת קובץ ${version.label}`}
          onClick={openPicker}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openPicker();
            }
          }}
          sx={{
            mx: 2.5,
            mb: 2.5,
            flex: 1,
            minHeight: 168,
            borderRadius: 2,
            border: `1.5px dashed ${isDragging ? version.color : "#cbd5e1"}`,
            bgcolor: isDragging ? "transparent" : "#fafbfc",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            gap: 0.75,
            px: 2,
            cursor: disabled ? "default" : "pointer",
            outline: "none",
            transition: "all .15s",
            "&:hover, &:focus-visible": !disabled && {
              borderColor: version.color,
              bgcolor: version.bg,
            },
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              bgcolor: version.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 0.5,
            }}
          >
            <CloudUploadOutlinedIcon sx={{ color: version.color, fontSize: 26 }} />
          </Box>
          <Typography fontWeight={600} color={TEXT_PRIMARY}>
            גררו קובץ PDF לכאן
          </Typography>
          <Typography variant="body2" color={TEXT_SECONDARY}>
            או <Box component="span" sx={{ color: version.color, fontWeight: 600 }}>לחצו לבחירה</Box>
          </Typography>
        </Box>
      ) : (
        <Fade in timeout={300}>
        <Box sx={{ mx: 2.5, mb: 2.5, flex: 1, display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              p: 1.75,
              borderRadius: 2,
              bgcolor: "#fafbfc",
              border: `1px solid ${BORDER}`,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: "#fef2f2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PictureAsPdfIcon sx={{ color: "#dc2626", fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Tooltip title={entry.file.name} placement="top">
                <Typography fontWeight={600} color={TEXT_PRIMARY} noWrap dir="auto" style={{ textAlign: "right" }}>
                  {entry.file.name}
                </Typography>
              </Tooltip>
              <Typography variant="caption" color={TEXT_SECONDARY}>
                {formatFileSize(entry.file.size)}
              </Typography>
            </Box>
            <Tooltip title="הסרת הקובץ">
              <span>
                <IconButton size="small" onClick={onClear} disabled={disabled} aria-label="הסרת הקובץ">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <ValidationStatus entry={entry} />

          <Box sx={{ mt: "auto", pt: 1.5 }}>
            <Button
              size="small"
              startIcon={<SwapHorizIcon />}
              onClick={openPicker}
              disabled={disabled}
              sx={{ color: TEXT_SECONDARY, fontWeight: 600, px: 1 }}
            >
              החלפת קובץ
            </Button>
          </Box>
        </Box>
        </Fade>
      )}
    </Paper>
  );
};

export default PdfVersionCard;
